import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Shield, Code2, Network, FileSearch, Palette, Cog } from 'lucide-react';

const services = [
  {
    icon: Shield,
    title: 'Cyber Security',
    color: '#007C89',
    desc: 'Comprehensive threat detection, vulnerability management, SOC setup, penetration testing, and security architecture tailored for your risk environment.',
    tags: ['Penetration Testing', 'SOC', 'Threat Intel', 'DLP'],
  },
  {
    icon: Code2,
    title: 'Software Development',
    color: '#008EA0',
    desc: 'Full-stack product engineering from SaaS platforms to enterprise applications — delivered iteratively with clean architecture and long-term maintainability.',
    tags: ['SaaS', 'Web Apps', 'APIs', 'DevOps'],
  },
  {
    icon: Network,
    title: 'Network Solutions',
    color: '#006D7B',
    desc: 'Network design, implementation, monitoring, and optimisation across LAN/WAN, SD-WAN, and cloud-hybrid environments for resilient infrastructure.',
    tags: ['LAN/WAN', 'SD-WAN', 'Monitoring', 'Cloud'],
  },
  {
    icon: FileSearch,
    title: 'IS Audit & Compliance',
    color: '#D97A00',
    desc: 'End-to-end information security audits, gap assessments, and compliance implementation for ISO 27001, DPDP Act 2023, GDPR, and SOC2.',
    tags: ['ISO 27001', 'DPDP 2023', 'GDPR', 'SOC2'],
  },
  {
    icon: Palette,
    title: 'UI Design',
    color: '#007C89',
    desc: 'Research-led interface design that balances aesthetic precision with functional clarity — from wireframes to production-ready design systems.',
    tags: ['Design Systems', 'UX Research', 'Prototyping', 'Branding'],
  },
  {
    icon: Cog,
    title: 'Process Optimisation',
    color: '#008EA0',
    desc: 'Identifying and eliminating inefficiencies through workflow re-engineering, automation integration, and data-driven continuous improvement strategies.',
    tags: ['Automation', 'Workflows', 'Analytics', 'BPM'],
  },
];

export default function Services() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="services" ref={ref} style={{ padding: '100px 5%', background: 'var(--navy)', position: 'relative', overflow: 'hidden' }}>
      {/* Section bg accent */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(0,172,193,0.24), transparent)' }} />

      <div style={{ maxWidth: 1280, margin: '0 auto' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}
          style={{ marginBottom: 72 }}>
          <span style={{ fontFamily: 'Space Mono', fontSize: 11, letterSpacing: '0.25em', color: 'var(--teal)', textTransform: 'uppercase' }}>
            — What We Do
          </span>
          <h2 style={{ fontSize: 'clamp(36px, 4vw, 56px)', fontWeight: 700, color: 'var(--text-main)', marginTop: 16, lineHeight: 1.1 }}>
            Our Service Lines
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 17, marginTop: 16, maxWidth: 560, fontWeight: 300, lineHeight: 1.7 }}>
            From securing infrastructure to building breakthrough software — every engagement is handled with the same standard of excellence.
          </p>
        </motion.div>

        {/* Cards grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 24 }}>
          {services.map((s, i) => (
            <ServiceCard key={s.title} s={s} i={i} inView={inView} />
          ))}
        </div>
      </div>

      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(0,172,193,0.16), transparent)' }} />
    </section>
  );
}

function ServiceCard({ s, i, inView }) {
  const Icon = s.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: i * 0.1 }}
      whileHover={{ y: -6, transition: { duration: 0.2 } }}
      style={{
        background: 'var(--navy-card)',
        border: '1px solid var(--navy-border)',
        borderRadius: 8,
        padding: '36px 32px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
        boxShadow: '0 16px 44px rgba(15,23,42,0.05)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = `${s.color}44`;
        e.currentTarget.style.boxShadow = `0 24px 52px rgba(15,23,42,0.1), 0 0 0 1px ${s.color}22`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--navy-border)';
        e.currentTarget.style.boxShadow = '0 16px 44px rgba(15,23,42,0.05)';
      }}
    >
      {/* Top accent line */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${s.color}, transparent)`, borderRadius: '8px 8px 0 0' }} />

      {/* Icon */}
      <div style={{
        width: 52, height: 52, borderRadius: 10,
        background: `${s.color}18`, border: `1px solid ${s.color}30`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 24,
      }}>
        <Icon size={24} color={s.color} />
      </div>

      <h3 style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 22, color: 'var(--text-main)', marginBottom: 14 }}>{s.title}</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: 14.5, lineHeight: 1.7, marginBottom: 24, fontWeight: 300 }}>{s.desc}</p>

      {/* Tags */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {s.tags.map(tag => (
          <span key={tag} style={{
            fontFamily: 'Space Mono', fontSize: 10, letterSpacing: '0.1em',
            color: s.color, background: `${s.color}12`,
            border: `1px solid ${s.color}25`,
            padding: '4px 10px', borderRadius: 2,
          }}>{tag}</span>
        ))}
      </div>
    </motion.div>
  );
}
